/**
 * Icon — the single SVG primitive every glyph in the MERIDIAN icon system renders
 * through. The grid (24), stroke weight (2), round caps/joins, currentColor, and the
 * accessibility contract live here and ONLY here, so there is one die stamping the
 * whole instrument face. Concrete icons (Search, StatusWarning, …) compose this and
 * supply only their paths.
 *
 * Design law (see src/components/icons/GALLERY.md and docs/design/MERIDIAN.md):
 *   • viewBox 0 0 24 24 — the authoring grid, always. Render size varies; the grid does not.
 *   • stroke-width 2, stroke=currentColor, fill=none for line icons (round cap/join).
 *   • currentColor everywhere → theme-safe by construction (light/dark/system inherit
 *     from the parent's resolved color; the theme mechanism only re-points tokens).
 *   • No hardcoded hex. Semantic status icons take --color-* from the CONSUMER, not here.
 *
 * A11y:
 *   • Default: decorative → aria-hidden="true" (the icon is a redundant accelerator next
 *     to a text label, or its control carries the aria-label).
 *   • Pass `title` to make it a labelled graphic → role="img" + <title> (use only when the
 *     icon is the sole label, which is rare — prefer an aria-label on the button instead).
 */
import type { SVGProps } from 'react';
import { ICON_SIZE_PX, type IconSize } from './constants';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> {
  /**
   * One of the fixed steps (xs 14 · sm 16 · md 20 · lg 24 · xl 32), or a raw px number
   * for the rare deliberately off-scale case (e.g. a 12px chip-close, a 40px hero glyph).
   * Prefer the enum. Default 'md'.
   */
  size?: IconSize | number;
  /**
   * When set, the icon becomes a labelled graphic (role="img" + <title>). When omitted
   * (the common case), the icon is aria-hidden and its control/label carries the meaning.
   */
  title?: string;
}

export const Icon = ({ size = 'md', title, className, children, ...rest }: IconProps) => {
  const px = typeof size === 'number' ? size : ICON_SIZE_PX[size];
  // fill/stroke are set BEFORE the spread so a consumer can override them (e.g. a filled
  // favorite star toggling fill between 'currentColor' and 'none'). The grid + size +
  // stroke-weight + caps are set AFTER the spread so they are non-overridable — the die.
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      {...rest}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      width={px}
      height={px}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...(title ? { role: 'img' } : { 'aria-hidden': true })}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
};

export default Icon;
